import {
  ShipmentContainerMediaContainerDetailDto,
  ShipmentContainerMediaByPhotoTypeDto,
  MediaByPhotoTypeDto,
  ShipmentContainerMediaInfoDto,
  ShipmentCustomPhotoTypeItemDto,
} from "@/src/store/apis/shipments/shipmentDetail/interface";
import {
  ContainerMediaData,
  ContainerMediaInfo,
  MediaByPhotoType,
  MediaByPhotoTypeItem,
  CustomPhotoTypeGroupItem,
} from "../../types";

/**
 * API 응답의 미디어 정보를 ContainerMediaInfo로 매핑
 * @param media - API 응답의 미디어 정보 객체
 * @returns 매핑된 ContainerMediaInfo
 */
const mapSingleMediaInfo = (
  media: ShipmentContainerMediaInfoDto
): ContainerMediaInfo => ({
  containerMediaId: media.containerMediaId,
  mediaUrl: media.mediaUrl,
  sSizeImageUrl: media.sSizeImageUrl,
  mSizeImageUrl: media.mSizeImageUrl,
  lSizeImageUrl: media.lSizeImageUrl,
  createdAt: media.createdAt,
  containerMediaType: media.containerMediaType,
  coordinate: media.coordinate,
});

/**
 * API 응답의 미디어 정보 리스트를 ContainerMediaInfo 배열로 매핑
 * @param list - API 응답의 미디어 리스트 (nullable)
 * @returns 매핑된 ContainerMediaInfo 배열
 */
export const mapMediaInfoList = (
  list?: ShipmentContainerMediaContainerDetailDto["sampleBaleMediaInfoList"]
): ContainerMediaInfo[] =>
  list?.map(mapSingleMediaInfo) ?? [];

/**
 * API 응답을 ContainerMediaData 타입으로 변환
 * 모든 adapter에서 공통으로 사용되는 매핑 로직
 *
 * @param response - 컨테이너 미디어 API 응답
 * @returns ContainerMediaData - 통합 타입으로 변환된 데이터
 *
 * @example
 * const response = await getSingleContainerMedia({ id: containerId }).unwrap();
 * const mapped = mapContainerMediaResponse(response);
 * setContainerMedia(mapped);
 */
export const mapContainerMediaResponse = (
  response: ShipmentContainerMediaContainerDetailDto
): ContainerMediaData => ({
  id: response.id,
  containerNo: response.containerNo,
  sealNo: response.sealNo,
  workingDayAt: response.workingDayAt,
  workplaceName: response.workplaceName,
  buyerName: response.buyerName,
  item: response.item,
  itemCode: response.itemCode ?? null,
  netWeight: response.netWeight,
  extraMediaRemark: response.extraMediaRemark,

  // Count fields
  containerSampleBaleMediasCount: response.containerSampleBaleMediasCount,
  containerEmptyMediasCount: response.containerEmptyMediasCount,
  containerQuarterLoadedMediasCount: response.containerQuarterLoadedMediasCount,
  containerHalfLoadedMediasCount: response.containerHalfLoadedMediasCount,
  containerFullLoadedMediasCount: response.containerFullLoadedMediasCount,
  containerOneDoorClosedMediasCount: response.containerOneDoorClosedMediasCount,
  containerBothDoorsClosedMediasCount: response.containerBothDoorsClosedMediasCount,
  containerCloseUpSealMediasCount: response.containerCloseUpSealMediasCount,
  containerExtraMediasCount: response.containerExtraMediasCount,

  // Uploaded count fields
  uploadedContainerSampleBaleMediasCount: response.uploadedContainerSampleBaleMediasCount,
  uploadedContainerEmptyMediasCount: response.uploadedContainerEmptyMediasCount,
  uploadedContainerQuarterLoadedMediasCount: response.uploadedContainerQuarterLoadedMediasCount,
  uploadedContainerHalfLoadedMediasCount: response.uploadedContainerHalfLoadedMediasCount,
  uploadedContainerFullLoadedMediasCount: response.uploadedContainerFullLoadedMediasCount,
  uploadedContainerOneDoorClosedMediasCount: response.uploadedContainerOneDoorClosedMediasCount,
  uploadedContainerBothDoorsClosedMediasCount: response.uploadedContainerBothDoorsClosedMediasCount,
  uploadedContainerCloseUpSealMediasCount: response.uploadedContainerCloseUpSealMediasCount,
  uploadedContainerExtraMediasCount: response.uploadedContainerExtraMediasCount,

  // Media info lists
  sampleBaleMediaInfoList: mapMediaInfoList(response.sampleBaleMediaInfoList),
  emptyMediaInfoList: mapMediaInfoList(response.emptyMediaInfoList),
  quarterLoadedMediaInfoList: mapMediaInfoList(response.quarterLoadedMediaInfoList),
  halfLoadedMediaInfoList: mapMediaInfoList(response.halfLoadedMediaInfoList),
  fullLoadedMediaInfoList: mapMediaInfoList(response.fullLoadedMediaInfoList),
  oneDoorClosedMediaInfoList: mapMediaInfoList(response.oneDoorClosedMediaInfoList),
  bothDoorsClosedMediaInfoList: mapMediaInfoList(response.bothDoorsClosedMediaInfoList),
  closeUpSealMediaInfoList: mapMediaInfoList(response.closeUpSealMediaInfoList),
  extraMediaInfoList: mapMediaInfoList(response.extraMediaInfoList),

  // Photo type medias (custom photo types)
  photoTypeMedias: response.photoTypeMedias,
});

/**
 * ShipmentContainerMediaInfoDto를 ContainerMediaInfo로 변환
 * @param media - API 응답의 미디어 정보
 * @returns 변환된 ContainerMediaInfo
 */
const mapShipmentMediaInfo = (
  media: ShipmentContainerMediaInfoDto
): ContainerMediaInfo => ({
  containerMediaId: media.containerMediaId,
  mediaUrl: media.mediaUrl,
  sSizeImageUrl: media.sSizeImageUrl,
  mSizeImageUrl: media.mSizeImageUrl,
  lSizeImageUrl: media.lSizeImageUrl,
  createdAt: media.createdAt,
  containerMediaType: media.containerMediaType,
  coordinate: media.coordinate,
});

/**
 * Photo Type별 미디어 아이템(MediaByPhotoTypeDto)을 MediaByPhotoTypeItem으로 매핑
 * @param items - API 응답의 MediaByPhotoTypeDto 배열 (nullable)
 * @returns 매핑된 MediaByPhotoTypeItem 배열
 */
const mapMediaByPhotoTypeItems = (
  items?: MediaByPhotoTypeDto[]
): MediaByPhotoTypeItem[] =>
  items?.map((item) => ({
    id: item.id,
    containerNo: item.containerNo ?? null,
    title: item.title,
    extraMediaRemark: item.extraMediaRemark,
    requiredContainerMediasCount: item.requiredContainerMediasCount,
    uploadedContainerMediasCount: item.uploadedContainerMediasCount,
    shipmentContainerMediaInfoList:
      item.shipmentContainerMediaInfoList?.map(mapShipmentMediaInfo) ?? [],
    displayOrder: item.displayOrder,
  })) ?? [];

const mapCustomPhotoTypeItems = (
  items?: ShipmentCustomPhotoTypeItemDto[]
): CustomPhotoTypeGroupItem[] =>
  items?.map((item) => ({
    id: item.id,
    containerNo: item.containerNo ?? null,
    title: item.title,
    subTitle: item.subTitle,
    extraMediaRemark: item.extraMediaRemark,
    requiredContainerMediasCount: item.requiredContainerMediasCount,
    uploadedContainerMediasCount: item.uploadedContainerMediasCount,
    shipmentContainerMediaInfoList:
      item.shipmentContainerMediaInfoList?.map(mapShipmentMediaInfo) ?? [],
    photoTypeRequestId: item.photoTypeRequestId,
    displayOrder: item.displayOrder,
  })) ?? [];

export const mapMediaByPhotoTypeResponse = (
  response: ShipmentContainerMediaByPhotoTypeDto
): MediaByPhotoType => ({
  sampleBale: mapMediaByPhotoTypeItems(response.sampleBale),
  empty: mapMediaByPhotoTypeItems(response.empty),
  quarterLoaded: mapMediaByPhotoTypeItems(response.quarterLoaded),
  halfLoaded: mapMediaByPhotoTypeItems(response.halfLoaded),
  fullLoaded: mapMediaByPhotoTypeItems(response.fullLoaded),
  oneDoorClosed: mapMediaByPhotoTypeItems(response.oneDoorClosed),
  bothDoorsClosed: mapMediaByPhotoTypeItems(response.bothDoorsClosed),
  closeUpSeal: mapMediaByPhotoTypeItems(response.closeUpSeal),
  extra: mapMediaByPhotoTypeItems(response.extra),
  customs: mapCustomPhotoTypeItems(response.customs),
});
